import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { LoginThemeService } from '../login-theme/login-theme.service';

/**
 * Servicio para preload de assets críticos (imágenes, iconos, fuentes)
 * Mejora el rendimiento precargando recursos importantes antes de que se necesiten
 */
@Injectable({
  providedIn: 'root',
})
export class PreloadService {
  private readonly document = inject(DOCUMENT);
  private readonly loginThemeService = inject(LoginThemeService);
  private readonly preloadedAssets = new Set<string>();

  /**
   * Precarga una imagen y la guarda en caché del navegador
   * @param url URL de la imagen a precargar
   * @returns Promise que se resuelve cuando la imagen está cargada
   */
  preloadImage(url: string): Promise<void> {
    if (this.preloadedAssets.has(url)) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        this.preloadedAssets.add(url);
        resolve();
      };
      img.onerror = reject;
      img.src = url;
    });
  }

  /**
   * Precarga múltiples imágenes en paralelo
   * @param urls Array de URLs de imágenes
   * @returns Promise que se resuelve cuando todas las imágenes están cargadas
   */
  preloadImages(urls: string[]): Promise<void[]> {
    return Promise.all(urls.map((url) => this.preloadImage(url)));
  }

  /**
   * Precarga un recurso usando un link rel="preload"
   * Útil para fuentes, CSS, JS, etc.
   * @param url URL del recurso
   * @param as Tipo de recurso (image, font, style, script, etc.)
   */
  preloadResource(url: string, as: string = 'image'): void {
    if (this.preloadedAssets.has(url)) {
      return;
    }

    const link = this.document.createElement('link');
    link.rel = 'preload';
    link.href = url;
    link.as = as;
    link.crossOrigin = 'anonymous';

    if (as === 'font') {
      link.type = 'font/woff2';
    }

    this.document.head.appendChild(link);
    this.preloadedAssets.add(url);
  }

  /**
   * Precarga las imágenes críticas de la aplicación
   * Se ejecuta al inicio de la app para mejorar el rendimiento
   */
  preloadCriticalAssets(): void {
    const loginUrls = this.loginThemeService.getLoginPreloadUrls();
    const criticalImages = [
      '/assets/logos/logo-palta.svg',
      '/assets/logos/logo.png',
      ...loginUrls,
      '/assets/images/cuenta-empresa.png',
      '/assets/images/tarjeta.png',
      '/assets/images/mano-chash.png',
    ];
    this.preloadImages(criticalImages).catch((error) => {
      console.warn('Error precargando imágenes críticas:', error);
    });
  }

  /**
   * Verifica si un asset ya fue precargado
   * @param url URL del asset
   * @returns true si ya fue precargado
   */
  isPreloaded(url: string): boolean {
    return this.preloadedAssets.has(url);
  }
}
